import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import Sidebar from '../Shared/Sidebar';
import Header from '../Shared/Header';
import './SubjectsDashboard.css';

// Subject color palettes and icons
const SUBJECT_CONFIG = {
    default: { gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', icon: '📖', light: '#ede9fe' },
    Mathematics: { gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', icon: '📐', light: '#fce7f3' },
    Science: { gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', icon: '🔬', light: '#e0f7fa' },
    Physics: { gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', icon: '⚛️', light: '#e8f5e9' },
    Chemistry: { gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', icon: '🧪', light: '#fff3e0' },
    Biology: { gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', icon: '🧬', light: '#e8f5e9' },
    History: { gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', icon: '🏛️', light: '#fff3e0' },
    Geography: { gradient: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)', icon: '🌏', light: '#e3f2fd' },
    English: { gradient: 'linear-gradient(135deg, #fd7043 0%, #ffa726 100%)', icon: '✍️', light: '#fff3e0' },
    Hindi: { gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', icon: '🪔', light: '#fce7f3' },
    'Social Science': { gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', icon: '🌐', light: '#e8eaf6' },
};

// Static recommended chapters (hardcoded data moved outside component)
const RECOMMENDED_CHAPTERS = [
    { id: 4, name: "Quadratic Equations", subject: "Mathematics", reason: "Weak in last test", level: "critical" },
    { id: 2, name: "Acids, Bases and Salts", subject: "Science", reason: "Focus recommended", level: "warning" },
    { id: 8, name: "Introduction to Trigonometry", subject: "Mathematics", reason: "Start with basics", level: "info" }
];

const getSubjectConfig = (name = '') => {
    const key = Object.keys(SUBJECT_CONFIG).find(k => name.toLowerCase().includes(k.toLowerCase()));
    return SUBJECT_CONFIG[key] || SUBJECT_CONFIG.default;
};

// Derive userId from localStorage once (outside component, runs once per module load)
const getUserId = () => {
    const userStr = localStorage.getItem('user');
    const userData = userStr ? JSON.parse(userStr) : null;
    return userData ? (userData.userId || userData.id) : '0';
};

const SubjectsDashboard = () => {
    const { t } = useTranslation(['subjects', 'dashboard', 'common', 'navigation']);
    const navigate = useNavigate();

    const [subjects, setSubjects] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [chaptersLoading, setChaptersLoading] = useState(false);
    const [completedChapters, setCompletedChapters] = useState({});
    const [showExceededModal, setShowExceededModal] = useState(false);
    const [startingChapterId, setStartingChapterId] = useState(null);

    const classId = localStorage.getItem('classId') || '';
    const studentClass = localStorage.getItem('studentClass') || '';
    const userId = useMemo(() => getUserId(), []);

    const subjectsCacheKey = `subjects_cache_${userId}_${classId}`;
    const completionsCacheKey = `completions_cache_${userId}`;

    // Load subjects from cache on mount
    useEffect(() => {
        const cached = localStorage.getItem(subjectsCacheKey);
        if (cached) {
            try {
                const { data, firstSubjectId } = JSON.parse(cached);
                setSubjects(data);
                if (data.length > 0) {
                    const first = data[0];
                    setSelectedSubject(first);
                    // Load chapters cache for first subject if available
                    const chCache = localStorage.getItem(`chapters_cache_${firstSubjectId}_${userId}`);
                    if (chCache) {
                        setChapters(JSON.parse(chCache));
                    }
                }
                setLoading(false);
            } catch (e) {
                console.error('Error parsing subjects cache:', e);
            }
        }

        const compCache = localStorage.getItem(completionsCacheKey);
        if (compCache) {
            try {
                setCompletedChapters(JSON.parse(compCache));
            } catch (e) {
                console.error('Error parsing completions cache:', e);
            }
        }
    }, [subjectsCacheKey, completionsCacheKey, userId]);

    const fetchCompletions = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}get_chapter_completion.php?user_id=${userId}`, { credentials: 'include' });
            const data = await res.json();
            if (data.status === 'success') {
                setCompletedChapters(data.completed || {});
                localStorage.setItem(completionsCacheKey, JSON.stringify(data.completed || {}));
            }
        } catch (err) {
            console.error('Could not load completions:', err);
        }
    }, [userId, completionsCacheKey]);

    const fetchChapters = useCallback(async (subjectId, isBackground = false) => {
        try {
            if (!isBackground) setChaptersLoading(true);
            const response = await fetch(`${API_BASE_URL}get_chapters.php?subject_id=${subjectId}&user_id=${userId}`, { credentials: 'include' });
            const data = await response.json();
            setChapters(data);
            localStorage.setItem(`chapters_cache_${subjectId}_${userId}`, JSON.stringify(data));
        } catch (error) {
            console.error("Error fetching chapters:", error);
        } finally {
            if (!isBackground) setChaptersLoading(false);
        }
    }, [userId]);

    const fetchSubjects = useCallback(async () => {
        try {
            const hasCachedSubjects = !!localStorage.getItem(subjectsCacheKey);
            if (!hasCachedSubjects) setLoading(true);

            const params = new URLSearchParams();
            if (classId) params.append('class_id', classId);
            if (studentClass) params.append('class_name', studentClass);

            const response = await fetch(`${API_BASE_URL}get_subjects.php?${params.toString()}`, { credentials: 'include' });
            const data = await response.json();
            setSubjects(data);

            if (data.length > 0) {
                setSelectedSubject(prev => prev || data[0]);
                // Cache subjects, also store the first subject id for cache-on-load
                localStorage.setItem(subjectsCacheKey, JSON.stringify({ data, firstSubjectId: data[0].id }));
                // Background-fetch chapters for the currently selected (or first) subject
                fetchChapters(data[0].id, hasCachedSubjects);
            }
        } catch (error) {
            console.error("Error fetching subjects:", error);
        } finally {
            setLoading(false);
        }
    }, [classId, studentClass, subjectsCacheKey, fetchChapters]);

    useEffect(() => {
        fetchSubjects();
        fetchCompletions();
    }, [fetchSubjects, fetchCompletions]);

    const handleSubjectClick = useCallback((subject) => {
        setSelectedSubject(subject);
        // Load chapters from cache if available, then background-fetch
        const chCache = localStorage.getItem(`chapters_cache_${subject.id}_${userId}`);
        if (chCache) {
            try {
                setChapters(JSON.parse(chCache));
                fetchChapters(subject.id, true); // background refresh
            } catch (e) {
                fetchChapters(subject.id);
            }
        } else {
            fetchChapters(subject.id);
        }
    }, [userId, fetchChapters]);

    const handleStartTest = useCallback(async (chapter) => {
        const boardName = localStorage.getItem('boardName') || 'CBSE';
        const className = localStorage.getItem('studentClass') || 'Class 10';
        const chapterId = chapter.chapter_id || chapter.id;

        setStartingChapterId(chapterId);
        try {
            const res = await fetch(`${API_BASE_URL}check_package_status.php?user_id=${userId}&chapter_id=${chapter.id}`, { credentials: 'include' });
            const data = await res.json();

            if (data.status === 'success') {
                if (data.exceeded && data.purchase_status !== 'active') {
                    setShowExceededModal(true);
                    setStartingChapterId(null);
                } else {
                    const finalSubject = chapter.subject || selectedSubject?.subject_name;
                    const finalSubjectId = chapter.subject_id || selectedSubject?.id;

                    navigate('/loader', {
                        state: {
                            board: boardName,
                            class: className,
                            subject: finalSubject,
                            subject_id: finalSubjectId,
                            chapter: chapter.chapter_name || chapter.name,
                            chapter_id: chapterId
                        }
                    });
                }
            } else {
                alert("Unable to verify account status.");
                setStartingChapterId(null);
            }
        } catch (err) {
            console.error("Error checking package status:", err);
            alert("Connection error.");
            setStartingChapterId(null);
        }
    }, [userId, selectedSubject, navigate]);

    const handleAnalyze = useCallback((chapter) => {
        const completion = completedChapters[chapter.id];
        navigate('/ai-doctor-report', {
            state: {
                mode: 'review',
                chapter_id: chapter.id,
                chapter: chapter.chapter_name,
                subject: selectedSubject?.subject_name,
                subject_id: selectedSubject?.id,
                test_id: completion?.test_id,
                score_percentage: completion?.score_percentage,
                correct_answers: completion?.correct_answers,
                total_questions: completion?.total_questions,
                board: localStorage.getItem('boardName') || 'CBSE',
                class: localStorage.getItem('studentClass') || 'Class 10',
            }
        });
    }, [completedChapters, selectedSubject, navigate]);

    return (
        <div className="dashboard-wrapper">
            <div className="dashboard-container">
                <Sidebar active="subjects" />

                <main className="main-content-area">
                    <Header showActions={true} hidePill={true} />

                    <div className="dashboard-scroll-container">
                        <div className="subjects-dashboard-content">
                            {/* 1. Recommended Chapters */}
                            <section className="dashboard-section-v3 recommendation-section">
                                <div className="section-header-v3">
                                    <h2>🎯 {t('subjects:subjects.recommendedChapters', 'Recommended Chapters')} <span className="beta-badge">{t('subjects:subjects.basedOnPerformance', 'Based on Performance')}</span></h2>
                                </div>

                                <div className="recommendations-grid">
                                    {loading && subjects.length === 0 ? (
                                        // Shimmer Loading State
                                        [1, 2, 3].map((i) => (
                                            <div key={i} className="rec-chapter-card shimmer">
                                                <div className="rec-card-header">
                                                    <div className="rec-tag shimmer-placeholder"></div>
                                                    <div className="rec-subject shimmer-placeholder"></div>
                                                </div>
                                                <div className="shimmer-placeholder h3" style={{ height: '24px', width: '80%', marginBottom: '1.5rem' }}></div>
                                                <div className="rec-action-btn shimmer-placeholder"></div>
                                            </div>
                                        ))
                                    ) : (
                                        RECOMMENDED_CHAPTERS.map((rect, i) => {
                                            const reasonKey = rect.reason.toLowerCase().includes('weak') ? 'weak' :
                                                            rect.reason.toLowerCase().includes('focus') ? 'focus' : 'basics';
                                            return (
                                                <div key={i} className={`rec-chapter-card ${rect.level}`}>
                                                    <div className="rec-card-header">
                                                        <span className="rec-tag">{t(`subjects:subjects.reasons.${reasonKey}`, rect.reason)}</span>
                                                        <span className="rec-subject">{t(`subjects:subjects.subjectNames.${rect.subject}`, rect.subject)}</span>
                                                    </div>
                                                    <h3>{rect.name}</h3>
                                                    <button className="rec-action-btn" onClick={() => handleStartTest({ id: rect.id, chapter_name: rect.name })}>
                                                        {t('subjects:subjects.fixThisNow', 'Fix This Now ⚡')}
                                                    </button>

                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </section>

                            {/* 2. All Subjects */}
                            <section className="dashboard-section-v3 subjects-explorer">
                                <div className="section-header-v3">
                                    <h2>📚 {t('subjects:subjects.exploreSubjects', 'Explore Subjects')}</h2>
                                    <p className="subtitle">{t('subjects:subjects.exploreSubtitle', 'Select a subject to view its chapter-wise diagnostic tests')}</p>
                                </div>


                                <div className="subjects-navigation-layout">
                                    {/* Sidebar-like subject list */}
                                    <div className="subjects-tabs">
                                        {loading && subjects.length === 0 ? (
                                            <div className="loading-shimmer-list">
                                                {[1, 2, 3, 4, 5].map(n => <div key={n} className="shimmer-item"></div>)}
                                            </div>
                                        ) : (
                                            subjects.map(s => {
                                                const cfg = getSubjectConfig(s.subject_name);
                                                return (
                                                    <button
                                                        key={s.id}
                                                        className={`subject-tab ${selectedSubject?.id === s.id ? 'active' : ''}`}
                                                        onClick={() => handleSubjectClick(s)}
                                                        style={selectedSubject?.id === s.id ? { '--tab-gradient': cfg.gradient } : {}}
                                                    >
                                                        <span className="subject-icon-badge" style={{ background: cfg.gradient }}>
                                                            {cfg.icon}
                                                        </span>
                                                        {t(`subjects:subjects.subjectNames.${s.subject_name}`, s.subject_name)}
                                                    </button>
                                                );
                                            })
                                        )}
                                    </div>

                                    {/* Chapter list area */}
                                    <div className="chapters-viewport">
                                        {selectedSubject && (() => {
                                            const cfg = getSubjectConfig(selectedSubject.subject_name);
                                            return (
                                                <div className="subject-header-mini">
                                                    <div className="subject-banner" style={{ background: cfg.gradient }}>
                                                        <span className="banner-icon">{cfg.icon}</span>
                                                        <div className="banner-info">
                                                            <h3>{t(`subjects:subjects.subjectNames.${selectedSubject.subject_name}`, selectedSubject.subject_name)}</h3>
                                                            <p>{chapters.length} {t(`subjects:subjects.${chapters.length === 1 ? 'chapter' : 'chapters'}`, 'Chapters')} {t('subjects:subjects.available', 'Available')}</p>
                                                        </div>

                                                        <span className="banner-chapter-count">{chapters.length}</span>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        <div className="chapters-grid">
                                            {chaptersLoading && chapters.length === 0 ? (
                                                <>
                                                    {[1, 2, 3, 4].map(n => <div key={n} className="shimmer-card"></div>)}
                                                </>
                                            ) : chapters.length > 0 ? (
                                                chapters.map((ch, i) => {
                                                    const cfg = getSubjectConfig(selectedSubject?.subject_name);
                                                    return (
                                                        <div key={ch.id} className={`chapter-card-v4 ${ch.is_locked ? 'locked' : ''}`} style={{ '--card-gradient': cfg.gradient, '--card-light': cfg.light }}>
                                                            {ch.is_locked && (
                                                                <div className="lock-overlay">
                                                                    <div className="lock-icon">🔒</div>
                                                                    <p>{t('subjects:subjects.premiumFeature', 'Premium Feature')}</p>
                                                                    <span>{t('subjects:subjects.testsCompleted', { count: 2, defaultValue: '2 Tests Completed' })}</span>
                                                                </div>

                                                            )}
                                                            <div className="card-accent-bar" style={{ background: cfg.gradient }}></div>
                                                            <div className="chapter-card-inner">
                                                                <div className="chap-meta">
                                                                    <span className="chap-num-badge" style={{ background: cfg.gradient }}>
                                                                        {String(ch.chapter_number || i + 1).padStart(2, '0')}
                                                                    </span>
                                                                    <span className="chap-label">{t('subjects:subjects.chapter', 'Chapter')}</span>
                                                                </div>

                                                                <h4 className="chap-title">{ch.chapter_name}</h4>
                                                                <div className="card-footer">
                                                                    {completedChapters[ch.id] ? (
                                                                        <>
                                                                            <div className="completion-badge">
                                                                                <span className="score-pill">
                                                                                    ✅ {completedChapters[ch.id].score_percentage}% {t('subjects:subjects.score', 'Score')}
                                                                                </span>
                                                                            </div>

                                                                            <div className="completed-actions-v4">
                                                                                <button
                                                                                    className="analyze-btn-v4"
                                                                                    onClick={() => handleAnalyze(ch)}
                                                                                >
                                                                                    🧠 {t('subjects:subjects.analyze', 'Analyze')}
                                                                                </button>

                                                                                <button
                                                                                    className="retake-btn-v4"
                                                                                    onClick={() => handleStartTest(ch)}
                                                                                    disabled={startingChapterId === (ch.chapter_id || ch.id)}
                                                                                >
                                                                                    {startingChapterId === (ch.chapter_id || ch.id) ? (
                                                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                                                            <span className="sd-spinner"></span>{t('subjects:subjects.loading', 'Loading...')}
                                                                                        </span>
                                                                                    ) : `⚡ ${t('subjects:subjects.retake', 'Retake')}`}
                                                                                </button>

                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <button
                                                                            className="test-now-btn"
                                                                            style={{ '--btn-gradient': cfg.gradient }}
                                                                            onClick={() => !ch.is_locked && handleStartTest(ch)}
                                                                            disabled={ch.is_locked || startingChapterId === (ch.chapter_id || ch.id)}
                                                                        >
                                                                            {startingChapterId === (ch.chapter_id || ch.id) ? (
                                                                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                                                                                    <span className="sd-spinner"></span>{t('subjects:subjects.loading', 'Loading...')}
                                                                                </span>
                                                                            ) : (
                                                                                <>
                                                                                    <span>{ch.is_locked ? t('subjects:subjects.locked', 'Locked') : t('subjects:subjects.startTest', 'Start Test')}</span>
                                                                                    <span className="btn-arrow">→</span>
                                                                                </>
                                                                            )}

                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="empty-state">
                                                    <div className="empty-icon">📭</div>
                                                    <p>{t('subjects:subjects.noChapters', 'No chapters available for this subject yet.')}</p>
                                                </div>

                                            )}
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>
                </main>

                {/* Premium Upsell Modal */}
                {showExceededModal && (
                    <div className="dr-modal-overlay">
                        <div className="dr-upsell-modal">
                            <div className="dr-modal-header">
                                <div className="dr-modal-icon">💎</div>
                                <h2>{t('dashboard:dashboard.premiumUpsell.title', 'Chapter Limit Reached')}</h2>
                                <button className="dr-modal-close" onClick={() => setShowExceededModal(false)}>✕</button>
                            </div>
                            <div className="dr-modal-body">
                                <p>{t('dashboard:dashboard.premiumUpsell.text', "You've utilized your free unique chapter quota. To continue re-taking tests and unlocking more chapters, upgrade to our Chapter Pack.")}</p>
                                <div className="dr-offer-card">
                                    <div className="dr-offer-info">
                                        <span className="dr-offer-title">{t('dashboard:dashboard.premiumUpsell.offerTitle', '8 Premium Chapters')}</span>
                                        <span className="dr-offer-price">{t('dashboard:dashboard.premiumUpsell.offerPrice', 'Only ₹20')}</span>
                                    </div>
                                    <div className="dr-offer-check">✓</div>
                                </div>
                                <ul className="dr-modal-features">
                                    <li>✨ {t('dashboard:dashboard.premiumUpsell.feature1', 'Unlimited Retakes for 8 Chapters')}</li>
                                    <li>📊 {t('dashboard:dashboard.premiumUpsell.feature2', 'Advanced AI Growth Insights')}</li>
                                    <li>🎯 {t('dashboard:dashboard.premiumUpsell.feature3', 'Personalized Practice Roadmap')}</li>
                                </ul>
                            </div>
                            <div className="dr-modal-footer">
                                <button className="dr-btn-primary full-width" style={{ background: 'linear-gradient(135deg, #00f2ff 0%, #3b82f6 100%)', color: '#000', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', width: '100%', marginBottom: '10px' }} onClick={() => navigate('/pricing')}>
                                    {t('dashboard:dashboard.premiumUpsell.button', 'Buy Chapter Pack Now')}
                                </button>
                                <button className="dr-link-btn" style={{ background: 'none', border: 'none', color: '#64748b', textDecoration: 'underline', cursor: 'pointer', display: 'block', margin: '0 auto' }} onClick={() => setShowExceededModal(false)}>
                                    {t('dashboard:dashboard.premiumUpsell.maybeLater', 'Maybe later')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubjectsDashboard;
